/**
 * 📏 MonKilometrage — v13.63 Phase 4 (2026-02-28)
 *
 * Affiche le kilométrage payable de la journée pour un employé terrain :
 * détail par segment (aller / inter / retour), franchise, km payables,
 * montant. Permet aussi d'ajouter un déplacement manuel entre 2 chantiers.
 *
 * Guardé par la clé RBAC `mon_espace_kilometrage` (côté conteneur parent).
 *
 * Ref : /app/memory/REGLE_KILOMETRAGE_V13_63.md
 */
import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import {
  MapPin, Home, Car, DollarSign, Plus, Trash2, Loader2, Info,
  AlertTriangle, ChevronDown, ChevronUp,
} from 'lucide-react';

import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';

const API_URL = (typeof window !== 'undefined' ? window.__API_URL__ : process.env.REACT_APP_BACKEND_URL) || '';

function fmt2(v) { return (v ?? 0).toFixed(2); }
function fmtMoney(v) { return `${fmt2(v)} $`; }

function SegmentRow({ segment }) {
  const isPaye = (segment.payable_km || 0) > 0;
  const typeLabel = { aller: 'Aller', retour: 'Retour', inter: 'Entre chantiers' }[segment.type] || segment.type;
  const typeColor = {
    aller: 'bg-blue-100 text-blue-700 border-blue-300',
    retour: 'bg-blue-100 text-blue-700 border-blue-300',
    inter: 'bg-emerald-100 text-emerald-700 border-emerald-300',
  }[segment.type] || 'bg-slate-100 text-slate-700 border-slate-300';
  return (
    <div
      className="rounded-lg border border-slate-200 bg-white p-3 space-y-2"
      data-testid={`km-segment-${segment.index ?? segment.id}`}
    >
      <div className="flex items-start justify-between gap-2">
        <Badge className={`text-[10px] ${typeColor}`}>{typeLabel}</Badge>
        {segment.source === 'manuel' && (
          <Badge className="text-[10px] bg-violet-50 text-violet-700 border-violet-300">
            Ajouté par vous
          </Badge>
        )}
      </div>
      <div className="text-xs space-y-1">
        <div className="flex items-start gap-1.5">
          <MapPin className="w-3 h-3 mt-0.5 text-slate-400 shrink-0" />
          <div className="flex-1 min-w-0">
            <p className="truncate text-slate-700">{segment.origine}</p>
            <p className="text-[10px] text-slate-400">↓</p>
            <p className="truncate text-slate-700">{segment.destination}</p>
          </div>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 pt-2 border-t border-slate-100 text-[11px]">
        <div>
          <p className="text-slate-500">Distance</p>
          <p className="font-semibold text-slate-800">
            {segment.distance_km != null ? `${fmt2(segment.distance_km)} km` : '—'}
          </p>
        </div>
        <div>
          <p className="text-slate-500">Franchise</p>
          <p className="font-semibold text-slate-800">
            {segment.franchise_km > 0 ? `${fmt2(segment.franchise_km)} km` : '—'}
          </p>
        </div>
        <div>
          <p className="text-slate-500">Payable</p>
          <p className={`font-semibold ${isPaye ? 'text-emerald-700' : 'text-slate-400'}`}>
            {fmt2(segment.payable_km)} km · {fmtMoney(segment.montant_dollars)}
          </p>
        </div>
      </div>
    </div>
  );
}

function AjoutManuelDialog({ open, onOpenChange, token, onDone, chantiersDuJour }) {
  const [origine, setOrigine] = useState('');
  const [destination, setDestination] = useState('');
  const [note, setNote] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [autoDetection, setAutoDetection] = useState(null);

  // 🎁 Détection auto : 2+ chantiers du jour → propose le déplacement pré-rempli
  useEffect(() => {
    if (chantiersDuJour && chantiersDuJour.length >= 2) {
      setAutoDetection({
        origine: chantiersDuJour[0],
        destination: chantiersDuJour[1],
      });
      setOrigine(chantiersDuJour[0].bt_id);
      setDestination(chantiersDuJour[1].bt_id);
    }
  }, [chantiersDuJour, open]);

  const handleSubmit = async () => {
    if (!origine || !destination || origine === destination) {
      toast.error('Choisissez 2 chantiers différents.');
      return;
    }
    setSubmitting(true);
    try {
      const today = new Date().toISOString().slice(0, 10);
      const r = await axios.post(`${API_URL}/api/mon-espace/kilometrage/ajouter-deplacement`,
        { date_iso: today, bt_id_origine: origine, bt_id_destination: destination, note: note || null },
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`Déplacement ajouté (${fmt2(r.data.segment.distance_km)} km).`);
      if (onDone) await onDone();
      onOpenChange?.(false);
      setOrigine(''); setDestination(''); setNote(''); setAutoDetection(null);
    } catch (e) {
      const msg = e?.response?.data?.detail || "Impossible d'ajouter le déplacement.";
      toast.error(msg);
    } finally { setSubmitting(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent data-testid="dialog-ajout-deplacement" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-base">
            <Plus className="w-4 h-4 text-emerald-600" />
            Ajouter un déplacement entre 2 chantiers
          </DialogTitle>
          <DialogDescription className="text-xs">
            Utile si vous êtes allé d'un chantier à un autre sans dé-puncher (retour matériel, aide urgente, etc.). La distance sera calculée automatiquement — vous n'avez pas de franchise sur ces déplacements inter-chantiers.
          </DialogDescription>
        </DialogHeader>

        {autoDetection && (
          <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-2 text-xs" data-testid="km-auto-detection">
            <div className="flex items-center gap-1.5 text-emerald-800 font-semibold mb-1">
              <Info className="w-3.5 h-3.5" /> Détection automatique
            </div>
            <p className="text-emerald-700">
              Nous avons trouvé 2 chantiers dans vos punchs du jour. Ajustez si besoin.
            </p>
          </div>
        )}

        <div className="space-y-3 pt-2">
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Chantier de départ</label>
            <select
              value={origine} onChange={(e) => setOrigine(e.target.value)}
              className="w-full border rounded-md px-2 py-1.5 text-sm"
              data-testid="select-origine"
            >
              <option value="">— Choisir —</option>
              {(chantiersDuJour || []).map((c) => (
                <option key={`o-${c.bt_id}`} value={c.bt_id}>
                  {c.client_nom || c.dossier_nom || 'Chantier'} — {c.adresse_travaux || ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Chantier de destination</label>
            <select
              value={destination} onChange={(e) => setDestination(e.target.value)}
              className="w-full border rounded-md px-2 py-1.5 text-sm"
              data-testid="select-destination"
            >
              <option value="">— Choisir —</option>
              {(chantiersDuJour || []).filter(c => c.bt_id !== origine).map((c) => (
                <option key={`d-${c.bt_id}`} value={c.bt_id}>
                  {c.client_nom || c.dossier_nom || 'Chantier'} — {c.adresse_travaux || ''}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label className="text-xs font-semibold text-slate-700 block mb-1">Note (facultatif)</label>
            <Textarea
              rows={2} maxLength={280}
              placeholder="Ex. : retour matériel au bureau"
              value={note} onChange={(e) => setNote(e.target.value)}
              data-testid="input-note"
            />
          </div>
          <Button
            onClick={handleSubmit}
            disabled={submitting || !origine || !destination}
            className="w-full bg-emerald-600 hover:bg-emerald-700"
            data-testid="btn-submit-deplacement"
          >
            {submitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
            Ajouter au kilométrage du jour
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export function MonKilometrage({ token }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [showAjout, setShowAjout] = useState(false);
  const [config, setConfig] = useState(null);

  const fetchData = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        axios.get(`${API_URL}/api/mon-espace/kilometrage/aujourd-hui`,
          { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API_URL}/api/config/kilometrage`,
          { headers: { Authorization: `Bearer ${token}` } }),
      ]);
      setData(r1.data);
      setConfig(r2.data);
    } catch (e) {
      // Erreur silencieuse — le composant reste inactif
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleDeleteManuel = async (seg_id) => {
    try {
      await axios.delete(`${API_URL}/api/mon-espace/kilometrage/manuel/${seg_id}`,
        { headers: { Authorization: `Bearer ${token}` } });
      toast.success('Déplacement retiré.');
      await fetchData();
    } catch {
      toast.error('Impossible de retirer ce déplacement.');
    }
  };

  if (loading && !data) {
    return (
      <Card data-testid="km-loading">
        <CardContent className="p-4 text-center text-sm text-slate-500">
          <Loader2 className="w-4 h-4 mx-auto animate-spin mb-2" />
          Calcul du kilométrage…
        </CardContent>
      </Card>
    );
  }

  if (!data) return null;

  // ─── Message doux : adresse domicile manquante ───────────────────────
  if (data.status === 'adresse_domicile_manquante') {
    return (
      <Card
        className="border-2 border-amber-200 bg-amber-50 dark:bg-amber-900/20"
        data-testid="km-status-adresse-manquante"
      >
        <CardContent className="p-4 flex items-start gap-3">
          <Home className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-bold text-amber-800">Kilométrage indisponible</p>
            <p className="text-xs text-amber-700 mt-1">{data.message}</p>
            <p className="text-[11px] text-amber-600 mt-2">
              💡 Vous pouvez la saisir vous-même depuis votre profil dans Mon Espace.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  // ─── Journée sans punch ──────────────────────────────────────────────
  if (data.status === 'aucun_punch_du_jour') {
    return (
      <Card className="border border-slate-200 bg-white" data-testid="km-status-aucun-punch">
        <CardContent className="p-4 flex items-start gap-3">
          <Car className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium text-slate-700">Aucun trajet aujourd'hui</p>
            <p className="text-xs text-slate-500 mt-1">
              Vous n'avez pas encore de punch chantier ce jour — rien à rembourser.
            </p>
          </div>
        </CardContent>
      </Card>
    );
  }

  const res = data.resultat || {};
  const segAuto = res.segments_auto || [];
  const segManuel = res.segments_manuels || [];
  const chantiersSansCoord = data.chantiers_sans_coord || [];
  const chantiersDuJour = data.chantiers_du_jour || [];
  const ajustement = data.ajustement;  // 🆕 [Phase 5] Ajustement admin s'il existe
  const kmFinal = data.km_payables_final ?? res.total_payable_km ?? 0;
  const mntFinal = data.montant_dollars_final ?? res.total_montant_dollars ?? 0;
  const isRefused = ajustement && ajustement.statut === 'refuse';

  return (
    <Card
      className="border-2 border-emerald-200 bg-gradient-to-br from-emerald-50 to-teal-50 dark:from-emerald-900/20 dark:to-teal-900/20"
      data-testid="km-card"
    >
      <CardContent className="p-4 space-y-3">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-2">
            <div className="w-10 h-10 rounded-xl bg-emerald-600 flex items-center justify-center shrink-0">
              <Car className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                Mon kilométrage
              </p>
              <p className="text-sm font-bold text-emerald-800">
                Aujourd'hui — {data.date_iso}
              </p>
            </div>
          </div>
          <div className="text-right">
            <p className="text-[10px] text-slate-500 uppercase">
              {ajustement ? 'À payer (ajusté)' : 'Total payable'}
            </p>
            <p
              className={`text-lg font-bold ${isRefused ? 'text-red-600' : ajustement ? 'text-amber-700' : 'text-emerald-700'}`}
              data-testid="km-total-montant"
            >
              {fmtMoney(mntFinal)}
            </p>
            <p className="text-[10px] text-slate-500">
              {fmt2(kmFinal)} km payables
            </p>
            {ajustement && (
              <Badge
                className={`text-[9px] mt-0.5 ${isRefused ? 'bg-red-100 text-red-700 border-red-300' : 'bg-amber-100 text-amber-800 border-amber-300'}`}
                data-testid="km-badge-ajuste-admin"
                title={ajustement.motif ? `Motif : ${ajustement.motif}` : ''}
              >
                {isRefused ? 'Refusé par l\'administration' : 'Ajusté par l\'administration'}
              </Badge>
            )}
          </div>
        </div>

        {/* 🆕 [Phase 5] Bandeau motif d'ajustement discret */}
        {ajustement && ajustement.motif && (
          <div
            className={`rounded-lg border px-2 py-1.5 text-[11px] ${isRefused ? 'border-red-200 bg-red-50 text-red-800' : 'border-amber-200 bg-amber-50 text-amber-800'}`}
            data-testid="km-motif-ajustement"
          >
            <span className="font-semibold">Motif :</span> {ajustement.motif}
            <p className="text-[10px] text-slate-500 mt-0.5">
              Valeur avant ajustement : {fmt2(ajustement.km_payables_avant)} km · {fmtMoney(ajustement.montant_dollars_avant)}
            </p>
          </div>
        )}

        {/* Alerte chantiers sans coords */}
        {chantiersSansCoord.length > 0 && (
          <div
            className="rounded-lg border border-amber-300 bg-amber-50 p-2 text-xs flex items-start gap-2"
            data-testid="km-alerte-sans-coord"
          >
            <AlertTriangle className="w-3.5 h-3.5 text-amber-600 shrink-0 mt-0.5" />
            <div className="flex-1">
              <p className="font-semibold text-amber-800">Certains chantiers sans GPS</p>
              <p className="text-amber-700">
                {data.message}
              </p>
            </div>
          </div>
        )}

        {/* Résumé compact + expand */}
        <div className="flex items-center justify-between text-xs">
          <button
            onClick={() => setExpanded(v => !v)}
            className="flex items-center gap-1 text-emerald-700 hover:text-emerald-800 font-medium"
            data-testid="btn-km-expand"
          >
            {expanded ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />}
            {expanded ? 'Masquer le détail' : `Voir le détail (${segAuto.length + segManuel.length} segments)`}
          </button>
          {config && (
            <p className="text-slate-500">
              Taux : <span className="font-semibold">{fmtMoney(config.taux_dollars_par_km)}</span>/km · Franchise : <span className="font-semibold">{fmt2(config.franchise_km_par_bout)} km</span> aller + retour
            </p>
          )}
        </div>

        {expanded && (
          <div className="space-y-2" data-testid="km-detail-container">
            {segAuto.map((s, i) => (
              <SegmentRow key={`auto-${i}`} segment={s} />
            ))}
            {segManuel.length > 0 && (
              <div className="pt-2 border-t border-emerald-200 space-y-2">
                <p className="text-[11px] font-semibold text-slate-600 uppercase">
                  Déplacements ajoutés manuellement
                </p>
                {segManuel.map((s) => (
                  <div key={s.id} className="relative">
                    <SegmentRow segment={s} />
                    <button
                      onClick={() => handleDeleteManuel(s.id)}
                      className="absolute top-2 right-2 p-1 rounded hover:bg-red-100 text-red-600"
                      title="Retirer ce déplacement"
                      data-testid={`btn-delete-manuel-${s.id}`}
                    >
                      <Trash2 className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        <Button
          onClick={() => setShowAjout(true)}
          size="sm"
          variant="outline"
          className="w-full border-emerald-300 text-emerald-700 hover:bg-emerald-100"
          data-testid="btn-ajouter-deplacement"
        >
          <Plus className="w-3.5 h-3.5 mr-1" />
          Ajouter un déplacement entre 2 chantiers
        </Button>
      </CardContent>

      <AjoutManuelDialog
        open={showAjout}
        onOpenChange={setShowAjout}
        token={token}
        onDone={fetchData}
        chantiersDuJour={chantiersDuJour}
      />
    </Card>
  );
}
